'use client';

import { usePathname, useRouter } from 'next/navigation';
import { PropsWithChildren, useEffect } from 'react';

import Loading from 'components/Loading';
import { FullscreenCenter } from 'components/shared-styles-v2';
import {
  OFFLINE_TEACHER_DASHBOARD_PATH,
  SHARED_CLASS_VIEW_PREFIX,
  UNAUTHORIZED_USER_PATH,
} from 'config/internalUrlPaths';
import { useTeacherContext, useUserContext } from 'context/Profile';
import { Permissions } from 'model/profiles';

const EXCLUDED_PATHS = [UNAUTHORIZED_USER_PATH];
const OFFLINE_PREFIX = '/offline';

const TeacherFirstRouteGuard = ({ children }: PropsWithChildren) => {
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useUserContext();
  const { hasPermission } = useTeacherContext();

  const hasPortal = user ? hasPermission(Permissions.VIEW_TEACHERS_PORTAL) : false;
  const hasOffline = user ? hasPermission(Permissions.VIEW_OFFLINE_TEACHERS_DASHBOARD) : false;

  const redirectTarget = (() => {
    if (!user || EXCLUDED_PATHS.some((path) => pathname?.startsWith(path))) {
      return null;
    }
    if (!hasPortal && !hasOffline) {
      return UNAUTHORIZED_USER_PATH;
    }
    // The shared-class view is neither a portal nor an offline path, so the two
    // routing redirects below must not fire for it - otherwise an offline-only
    // teacher opening a share link is bounced to their dashboard. The
    // unauthorized check above still applies: a teacher permission is required.
    if (pathname?.startsWith(SHARED_CLASS_VIEW_PREFIX)) {
      return null;
    }
    if (!hasPortal && hasOffline && !pathname?.startsWith(OFFLINE_PREFIX)) {
      return OFFLINE_TEACHER_DASHBOARD_PATH;
    }
    if (hasPortal && !hasOffline && pathname?.startsWith(OFFLINE_PREFIX)) {
      return UNAUTHORIZED_USER_PATH;
    }
    return null;
  })();

  useEffect(() => {
    if (redirectTarget) {
      router.replace(redirectTarget);
    }
  }, [redirectTarget, router]);

  if ((!user || redirectTarget) && !pathname?.includes(UNAUTHORIZED_USER_PATH)) {
    return (
      <FullscreenCenter>
        <Loading />
      </FullscreenCenter>
    );
  }

  return children;
};

export default TeacherFirstRouteGuard;
